import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, HStack, IconButton, Spinner, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import { HiOutlineClock, HiOutlineDocumentCheck, HiOutlinePlusCircle, HiOutlineShieldCheck, HiOutlineTrash } from "react-icons/hi2";
import { useAuth } from "@/auth/AuthContext";
import AccountService from "@/services/AccountService";
import { toaster } from "@/components/ui/toaster";
import { Field, FormCheckbox, FormTextarea } from "@/components/jobOffer/ui/inputs";
import SectionCard from "./SectionCard";

const empty = {
    gdprConsentText: "",
    privacyPolicyConsentText: "",
    privacyPolicyContent: "",
    privacyPolicyRequired: true,
    customConsents: [],
};

const MAX_CUSTOM_CONSENTS = 10;

const toForm = (data) => ({
    gdprConsentText: data?.gdprConsentText ?? "",
    privacyPolicyConsentText: data?.privacyPolicyConsentText ?? "",
    privacyPolicyContent: data?.privacyPolicyContent ?? "",
    privacyPolicyRequired: data?.privacyPolicyRequired ?? true,
    customConsents: (data?.customConsents ?? []).map((c) => ({
        id: c.id, text: c.text ?? "", required: Boolean(c.required), version: c.version,
    })),
});

const CODE_ORDER = ["gdpr", "privacy_policy"];
const isCustom = (code) => code.startsWith("custom");

const excerpt = (text) => ((text ?? "").length > 50 ? `${text.slice(0, 50)}…` : text ?? "");

// Versions come from the server and change on every saved edit.
const VersionHistory = ({ history, onRestore }) => {
    const { t } = useTranslation("account");
    if (!history.length) {
        return <Text color="text.grey" fontSize="sm">{t("settingsPage.consents.historyEmpty")}</Text>;
    }
    const customCodes = [...new Set(history.filter((v) => isCustom(v.code)).map((v) => v.code))];
    return (
        <VStack w="full" align="stretch" gap={5}>
            {[...CODE_ORDER, ...customCodes].map((code) => {
                const rows = history.filter((v) => v.code === code);
                if (!rows.length) return null;
                return (
                    <Box key={code}>
                        <Text fontWeight="600" fontSize="sm" mb={2}>
                            {isCustom(code)
                                ? `${t("settingsPage.consents.historyCode.custom")}: ${excerpt(rows[0].text)}`
                                : t(`settingsPage.consents.historyCode.${code}`)}
                        </Text>
                        <VStack align="stretch" gap={2}>
                            {rows.map((v) => (
                                <Box key={v.version} as="details" borderWidth="1px" borderRadius="md" px={3} py={2}>
                                    <Box as="summary" cursor="pointer" fontSize="sm">
                                        <Text as="span" fontWeight="600">{v.version}</Text>
                                        <Text as="span" color="text.grey"> · {dayjs(v.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
                                        {v.required && <Text as="span" color="text.grey"> · {t("settingsPage.consents.requiredBadge")}</Text>}
                                    </Box>
                                    {v.text && <Text mt={2} fontSize="sm" whiteSpace="pre-wrap">{v.text}</Text>}
                                    {v.content && <Text mt={2} fontSize="xs" color="text.grey" whiteSpace="pre-wrap" maxH="240px" overflowY="auto">{v.content}</Text>}
                                    {onRestore && (
                                        <Button mt={2} size="xs" variant="outlined" type="button" onClick={() => onRestore(code, v)}>
                                            {t("settingsPage.consents.restoreButton")}
                                        </Button>
                                    )}
                                </Box>
                            ))}
                        </VStack>
                    </Box>
                );
            })}
        </VStack>
    );
};

// Blank fields fall back to the chat's default texts.
const ChatConsentsSection = () => {
    const { t } = useTranslation("account");
    const { isOwner } = useAuth();

    const [saved, setSaved] = useState(empty);
    const [form, setForm] = useState(empty);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [versions, setVersions] = useState({});
    const [history, setHistory] = useState([]);

    const loadHistory = () =>
        AccountService.getChatConsentHistory().then((data) => setHistory(Array.isArray(data) ? data : [])).catch(() => {});

    useEffect(() => {
        AccountService.getChatConsents()
            .then((data) => {
                setSaved(toForm(data));
                setForm(toForm(data));
                setVersions(data ?? {});
            })
            .catch(() => toaster.create({ type: "error", title: t("settingsPage.consents.toastLoadError") }))
            .finally(() => setIsLoading(false));
        loadHistory();
    }, []);

    const isDirty = JSON.stringify(form) !== JSON.stringify(saved);
    const disabled = !isOwner || isSaving;
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const setCustom = (i, patch) =>
        setForm((f) => ({ ...f, customConsents: f.customConsents.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
    const addCustom = () =>
        setForm((f) => ({ ...f, customConsents: [...f.customConsents, { id: null, text: "", required: false, version: null }] }));
    const removeCustom = (i) =>
        setForm((f) => ({ ...f, customConsents: f.customConsents.filter((_, j) => j !== i) }));

    // Loads an old version into the form; it becomes the current one only after saving.
    const restore = (code, v) => {
        setForm((f) => {
            if (code === "gdpr") return { ...f, gdprConsentText: v.text ?? "" };
            if (code === "privacy_policy") return { ...f, privacyPolicyConsentText: v.text ?? "", privacyPolicyContent: v.content ?? "" };
            const id = code.split(":")[1];
            return { ...f, customConsents: f.customConsents.map((c) => (c.id === id ? { ...c, text: v.text ?? "", required: Boolean(v.required) } : c)) };
        });
        toaster.create({ type: "info", title: t("settingsPage.consents.toastRestored", { version: v.version }) });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const data = await AccountService.updateChatConsents(form);
            setSaved(toForm(data));
            setForm(toForm(data));
            setVersions(data ?? {});
            await loadHistory();
            toaster.create({ type: "success", title: t("settingsPage.consents.toastSaveSuccess") });
        } catch {
            toaster.create({ type: "error", title: t("settingsPage.consents.toastSaveError") });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <HStack w="full" py={8} justify="center">
                <Spinner color="brand.400" size="md" />
            </HStack>
        );
    }

    return (
        <VStack w="full" gap={4} align="flex-start">
            {!isOwner && (
                <Text color="text.grey" fontSize="sm">{t("settingsPage.consents.ownerOnly")}</Text>
            )}

            <SectionCard
                icon={<HiOutlineShieldCheck />}
                title={t("settingsPage.consents.gdprTitle")}
                subtitle={t("settingsPage.consents.gdprSubtitle")}
            >
                <Field label={t("settingsPage.consents.gdprLabel")} hint={t("settingsPage.consents.gdprHint")}>
                    <FormTextarea
                        rows={4}
                        value={form.gdprConsentText}
                        onChange={set("gdprConsentText")}
                        disabled={disabled}
                        maxLength={5000}
                        placeholder={t("settingsPage.consents.gdprPlaceholder")}
                    />
                </Field>
                <Text fontSize="xs" color="text.grey" mt={3}>
                    {t("settingsPage.consents.currentVersion", { version: versions.gdprVersion ?? "-" })}
                </Text>
            </SectionCard>

            <SectionCard
                icon={<HiOutlineDocumentCheck />}
                title={t("settingsPage.consents.policyTitle")}
                subtitle={t("settingsPage.consents.policySubtitle")}
            >
                <Box mb={4}>
                    <Field label={t("settingsPage.consents.privacyLabel")} hint={t("settingsPage.consents.privacyHint")}>
                        <FormTextarea
                            rows={2}
                            value={form.privacyPolicyConsentText}
                            onChange={set("privacyPolicyConsentText")}
                            disabled={disabled}
                            maxLength={1000}
                            placeholder={t("settingsPage.consents.privacyPlaceholder")}
                        />
                    </Field>
                </Box>
                <Field label={t("settingsPage.consents.policyLabel")} hint={t("settingsPage.consents.policyHint")}>
                    <FormTextarea
                        rows={14}
                        resize="vertical"
                        value={form.privacyPolicyContent}
                        onChange={set("privacyPolicyContent")}
                        disabled={disabled}
                        maxLength={100000}
                        placeholder={t("settingsPage.consents.policyPlaceholder")}
                    />
                </Field>
                <Box mt={3}>
                    <FormCheckbox
                        label={t("settingsPage.consents.customRequired")}
                        checked={form.privacyPolicyRequired}
                        onChange={(checked) => !disabled && setForm((f) => ({ ...f, privacyPolicyRequired: checked }))}
                    />
                </Box>
                <Text fontSize="xs" color="text.grey" mt={3}>
                    {t("settingsPage.consents.currentVersion", { version: versions.privacyPolicyVersion ?? "-" })}
                </Text>
            </SectionCard>

            <SectionCard
                icon={<HiOutlinePlusCircle />}
                title={t("settingsPage.consents.customTitle")}
                subtitle={t("settingsPage.consents.customSubtitle", { max: MAX_CUSTOM_CONSENTS })}
            >
                <VStack align="stretch" gap={4}>
                    {form.customConsents.map((c, i) => (
                        <Box key={c.id ?? `new-${i}`} borderWidth="1px" borderRadius="md" p={3}>
                            <HStack align="flex-start" gap={2}>
                                <FormTextarea
                                    rows={3}
                                    value={c.text}
                                    onChange={(e) => setCustom(i, { text: e.target.value })}
                                    disabled={disabled}
                                    maxLength={1000}
                                    placeholder={t("settingsPage.consents.customPlaceholder")}
                                />
                                {isOwner && (
                                    <IconButton size="sm" variant="ghost" type="button" disabled={isSaving}
                                        aria-label={t("settingsPage.consents.customRemove")} onClick={() => removeCustom(i)}>
                                        <HiOutlineTrash />
                                    </IconButton>
                                )}
                            </HStack>
                            <HStack mt={2} justify="space-between">
                                <FormCheckbox
                                    label={t("settingsPage.consents.customRequired")}
                                    checked={c.required}
                                    onChange={(checked) => !disabled && setCustom(i, { required: checked })}
                                />
                                {c.version && (
                                    <Text fontSize="xs" color="text.grey">
                                        {t("settingsPage.consents.currentVersion", { version: c.version })}
                                    </Text>
                                )}
                            </HStack>
                        </Box>
                    ))}
                    {!form.customConsents.length && (
                        <Text color="text.grey" fontSize="sm">{t("settingsPage.consents.customEmpty")}</Text>
                    )}
                    {isOwner && (
                        <Box>
                            <Button size="sm" variant="outlined" type="button" disabled={isSaving || form.customConsents.length >= MAX_CUSTOM_CONSENTS} onClick={addCustom}>
                                <HiOutlinePlusCircle /> {t("settingsPage.consents.customAdd")}
                            </Button>
                        </Box>
                    )}
                </VStack>
            </SectionCard>

            <SectionCard
                icon={<HiOutlineClock />}
                title={t("settingsPage.consents.historyTitle")}
                subtitle={t("settingsPage.consents.historySubtitle")}
            >
                <VersionHistory history={history} onRestore={isOwner ? restore : null} />
            </SectionCard>

            {isOwner && (
                <HStack w="full" justify="flex-end" gap={2}>
                    <Button variant="outlined" size="sm" onClick={() => setForm(saved)} disabled={!isDirty || isSaving} type="button">
                        {t("settingsPage.cancelButton")}
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving} disabled={!isDirty} type="button">
                        {t("settingsPage.saveButton")}
                    </Button>
                </HStack>
            )}
        </VStack>
    );
};

export default ChatConsentsSection;
